import { useId, useRef } from "react";
import { Button, Dialog, TextButton } from "@/components/ui";
import type { PublicPlatform } from "@/lib/api/types";
import { josa } from "@/lib/josa";
import { openUrlFor, stepsFor } from "@/lib/platform";
import { copyText } from "../copyFlow";

export interface CopyResult {
  platform: PublicPlatform;
  ok: boolean;
  copied: string;
  title: string;
  restoredTail: boolean;
}

/**
 * Confirms the copy before anyone leaves the page: what was copied, the platform's own steps,
 * then a second press to open it. If copying failed, the text is offered for a manual copy.
 */
export function CopyConfirmDialog({
  result,
  mode,
  onClose,
  onOpen,
}: {
  result: CopyResult | null;
  mode: "live" | "preview" | "demo";
  onClose: () => void;
  onOpen: (p: PublicPlatform) => void;
}) {
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const stepsId = useId();
  if (!result) return null;
  const { platform, ok, copied, title } = result;
  const url = openUrlFor(platform);
  const steps = stepsFor(platform);

  const selectAll = () => {
    areaRef.current?.focus();
    areaRef.current?.select();
  };

  const primary =
    url && mode !== "demo" ? (
      <Button
        ref={primaryRef}
        size="large"
        wrap
        fullWidth
        onClick={() => onOpen(platform)}
        iconRight="arrowUpRight"
      >
        {platform.name} 열기
      </Button>
    ) : (
      <Button ref={primaryRef} size="large" fullWidth onClick={onClose}>
        확인했어요
      </Button>
    );

  return (
    <Dialog
      open
      width={440}
      title={ok ? "복사했어요" : "복사가 되지 않았어요"}
      description={
        ok
          ? `고마워요. 이제 ${platform.name}에 붙여 넣기만 하면 돼요.`
          : "아래 글을 길게 눌러 전체 선택한 뒤 복사해 주세요."
      }
      onClose={onClose}
      initialFocusRef={primaryRef}
      primaryAction={primary}
    >
      <div className="flex flex-col gap-5 break-keep">
        {result.restoredTail ? (
          <p className="text-label-1 text-label-neutral">
            지워진 제공받음 문장을 다시 붙였어요. 이 문장은 꼭 남겨 주세요.
          </p>
        ) : null}

        <section aria-label="복사한 글">
          {title ? (
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="font-semibold text-label-normal">제목: {title}</p>
              <TextButton size="small" onClick={() => void copyText(title)}>
                제목 복사하기
              </TextButton>
            </div>
          ) : null}
          {ok ? (
            <blockquote className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md bg-fill-normal p-4 text-label-normal">
              {copied}
            </blockquote>
          ) : (
            <textarea
              ref={areaRef}
              readOnly
              value={copied}
              rows={5}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="복사할 글"
              className="w-full resize-none rounded-md bg-fill-normal p-4 text-label-normal"
            />
          )}
        </section>

        <section aria-labelledby={stepsId}>
          <h3 id={stepsId} className="font-semibold text-label-normal">
            {platform.name}에서는 이렇게 해 주세요
          </h3>
          <ol className="mt-2 flex flex-col gap-2">
            {steps.map((s, i) => (
              <li key={s} className="flex gap-3">
                <span
                  aria-hidden
                  className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-fill-strong text-label-2 font-semibold text-label-normal"
                >
                  {i + 1}
                </span>
                <span className="text-label-normal">{s}</span>
              </li>
            ))}
          </ol>
          {!url ? (
            <p className="mt-3 text-label-1 text-label-neutral">
              {josa(platform.name, "은/는")} 주문하거나 이용한 계정에서만 후기를 쓸 수 있어서, 앱을
              직접 열어 주세요.
            </p>
          ) : null}
          {mode === "demo" ? (
            <p className="mt-3 text-label-1 text-label-neutral">
              체험 화면이라 실제로 열지는 않아요.
            </p>
          ) : null}
        </section>

        {ok ? (
          <details className="rounded-md p-0">
            <summary className="flex min-h-12 items-center font-semibold text-primary-normal">
              복사가 안 됐나요?
            </summary>
            <div className="mt-1 flex flex-col gap-3">
              <p className="text-label-neutral">
                아래 칸을 길게 누르고 전체 선택, 복사를 차례로 눌러 주세요.
              </p>
              <textarea
                ref={areaRef}
                readOnly
                value={copied}
                rows={4}
                onFocus={(e) => e.currentTarget.select()}
                aria-label="복사할 글"
                className="w-full resize-none rounded-md bg-fill-normal p-4 text-label-normal"
              />
              <Button variant="outlined" color="assistive" size="large" onClick={selectAll}>
                글 전체 선택하기
              </Button>
            </div>
          </details>
        ) : (
          <Button variant="outlined" color="assistive" size="large" onClick={selectAll}>
            글 전체 선택하기
          </Button>
        )}
      </div>
    </Dialog>
  );
}
